import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConfigs } from '../../../hooks/useGetConfigs';
import { SettingsItemSubTitle, SettingsItemTitle } from '../../settings/SettingsItem';
import { Flex } from '../../../components/ui/Flex';
import { getIntercomUnitPins } from '../../../redux/actions/intercom';
import { HeaderText, ModalBodyContent, ModalHeader, StyledModalBody, StyledModalV2 } from '../../../components/Modals';
import LoadingAvatar from '../../../components/LoadingAvatar';
import { requestPending } from '../../../utils/status';
import { GET_INTERCOM_UNIT_PINS } from '../../../redux/constants';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { closeActiveModal } from '../../../helper/modals';
import styled from 'styled-components';

const ViewUnitIntercomPinsModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { config } = useGetConfigs();
  const { modal, data: unit } = useSelector((state) => state.headerModal);
  const selectedBuilding = useSelector((state) => state.building.globalSelectedBuilding);
  const building = useSelector((state) => state.building.building);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);

  const intercomData = useSelector((state) => state.intercom);

  const pinRender = useMemo(() => {
    if (intercomData.getIntercomUnitPinsError) {
      return <HeaderText alt>{t('Error fetching intercom pins')}</HeaderText>;
    }

    const filteredPins = intercomData.getIntercomUnitPinsData.filter((pin) => Boolean(pin.private_pin));
    if (!filteredPins.length) {
      return <HeaderText alt>{t('No intercom pins found')}</HeaderText>;
    }

    return (
      <Flex direction="column" justify="center">
        {filteredPins.map((pin) => (
          <PinItem direction="column" justify="center" key={pin.id}>
            <SettingsItemTitle>{pin.full_name}</SettingsItemTitle>
            <SettingsItemSubTitle>{pin.private_pin}</SettingsItemSubTitle>
          </PinItem>
        ))}
      </Flex>
    )
  }, [intercomData.getIntercomUnitPinsData]);

  useEffect(() => {
    if (unit.id && config?.enable_akuvox && config?.enable_akuvox_private_pins) {
      dispatch(getIntercomUnitPins({
        orgId: globalOrgId,
        buildingId: building?.id || selectedBuilding.id,
        unitID: unit.id
      }));
    }
  }, [unit.id, config]);

  return (
    <StyledModalV2
      show={modal === 'ViewUnitIntercomPinsModal'}
      onClose={closeActiveModal}
      closeOnBackdrop={true}
      width={'550px'}
      side
    >
      <StyledModalBody>
        <ModalHeader v2 title={`${t('Pins for unit')} ${unit.unit_num}`} />
        {intercomData.getIntercomUnitPinsStatus === requestPending(GET_INTERCOM_UNIT_PINS) ? (
          <LoadingAvatar color={LOADING_BLUE} />
        ) : (
          <ModalBodyContent>
            {pinRender}
          </ModalBodyContent>
        )}
      </StyledModalBody>
    </StyledModalV2>
  );
};

const PinItem = styled(Flex)`
  &:not(:first-of-type) {
    padding-top: 24px;
    border-top: 1px solid #D4D4D4;
  }

  :not(:last-of-type) {
    padding-bottom: 24px;
  }  
`;

export default ViewUnitIntercomPinsModal;
